import { useState } from 'react';
import useStore from '../../store/useStore';
import Modal from '../ui/Modal';
import GlowColorPicker from './GlowColorPicker';
import StylePicker from './StylePicker';

/**
 * Static Henrich & Denzel contact details, taken verbatim from the public
 * Impressum (henrich-denzel.com/impressum.html). No backend / form / mailto —
 * the modal just surfaces who to reach and how.
 */
const HD_CONTACT = {
    company: 'Henrich & Denzel',
    management: 'Christian Brüggerhoff',
    email: 'info@henrich-denzel.de',
    phone: '+49 7732 94 92 0',
    intro: 'Sprich direkt mit der Manufaktur — wir beraten dich persönlich zu deinem Ring.',
} as const;

/**
 * Simple configurator — the bottom section of the landing page.
 *
 * Canvas pointer-events are re-enabled here (see index.css #three-canvas-root)
 * so users can orbit the ring model while configuring.
 *
 * Options exposed:
 *  - Glow colour (fluorescent material, dark mode)
 *  - Gem selection (stone type and size)
 *
 * The canvas behind this section shows the ring at the "configurator" camera
 * beat (scroll 1.0) so the 3D view matches what the user is configuring.
 */
export default function SimpleConfigurator() {
    const isDark = useStore((s) => s.isDark);
    const scrollProgress = useStore((s) => s.scrollProgress);
    const [contactOpen, setContactOpen] = useState(false);

    // Slide in when the user reaches the last ~20% of scroll travel.
    const visible = scrollProgress >= 0.8;

    return (
        <>
        <div
            id="configurator"
            aria-label="Konfigurator"
            style={{ pointerEvents: visible ? 'auto' : 'none' }}
            className={[
                'fixed bottom-0 right-0 z-10 flex justify-end px-8 pb-6 md:px-20',
                'w-full transition-transform duration-500 ease-out',
                visible ? 'translate-y-0' : 'translate-y-full',
            ].join(' ')}
        >
            {/* Panel */}
            <div
                className={[
                    'w-full max-w-sm rounded-2xl p-8 backdrop-blur-md',
                    'border border-[var(--border)] bg-[var(--surface)] bg-opacity-80',
                    'transition-colors duration-800',
                ].join(' ')}
            >
                <p className="mb-1 text-xs tracking-[0.3em] uppercase text-[var(--fg-muted)]">
                    Konfigurieren
                </p>
                <h2 className="mb-8 font-serif text-3xl text-[var(--fg)]">
                    Dein Ring
                </h2>

                <div className="space-y-8">
                    {/* Glow colour — always shown, glows in dark mode */}
                    <GlowColorPicker />

                    {/* Unified style picker: Stein | Rille | Welle */}
                    <StylePicker />

                    {isDark && (
                        <p className="text-xs text-[var(--fg-muted)] italic">
                            Im Dunkeln leuchtet der Stein in deiner gewählten Farbe.
                        </p>
                    )}
                </div>

                <div className="mt-10">
                    <button
                        type="button"
                        className={[
                            'w-full rounded-full py-3 text-sm tracking-widest uppercase transition-all duration-300',
                            isDark
                                ? 'bg-[var(--glow-color)] text-black shadow-[0_0_20px_var(--glow-color)]'
                                : 'bg-[var(--fg)] text-[var(--bg)]',
                        ].join(' ')}
                        onClick={() => setContactOpen(true)}
                    >
                        Mit H&D sprechen
                    </button>
                </div>
            </div>
        </div>

        <Modal
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            labelledById="contact-modal-title"
        >
            <p className="mb-1 text-xs tracking-[0.3em] uppercase text-[var(--fg-muted)]">
                Kontakt
            </p>
            <h2
                id="contact-modal-title"
                className="mb-4 font-serif text-2xl text-[var(--fg)]"
            >
                {HD_CONTACT.company}
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-[var(--fg-muted)]">
                {HD_CONTACT.intro}
            </p>
            <dl className="space-y-4 text-sm">
                <div>
                    <dt className="text-xs tracking-widest uppercase text-[var(--fg-muted)]">
                        Geschäftsführer
                    </dt>
                    <dd className="mt-1 text-[var(--fg)]">{HD_CONTACT.management}</dd>
                </div>
                <div>
                    <dt className="text-xs tracking-widest uppercase text-[var(--fg-muted)]">
                        E-Mail
                    </dt>
                    <dd className="mt-1 text-[var(--fg)]">{HD_CONTACT.email}</dd>
                </div>
                <div>
                    <dt className="text-xs tracking-widest uppercase text-[var(--fg-muted)]">
                        Telefon
                    </dt>
                    <dd className="mt-1 text-[var(--fg)]">{HD_CONTACT.phone}</dd>
                </div>
            </dl>
        </Modal>
        </>
    );
}
